// New Scenario creation dialog — styled to match NewClientDialog so the
// Scenarios & Staff Plans landing has a consistent "create" experience.
//
// Lets the user pick:
//   - Workspace: Basic (staff plan + OGS + automation) or Advanced (full profile)
//   - Scenario name (required)
//   - Optional notes
//   - Template: clone an existing scenario, or start blank
//   - Model (Advanced / blank) or Staff plan (Basic blank — model follows the plan)

import { useMemo, useState } from 'react';
import { Copy, FilePlus2, X } from 'lucide-react';
import clsx from 'clsx';
import type { Client, Scenario, ScenarioWorkspace, StaffPlan } from '@rc/engine/types';
import { flattenStaffPlanToByLevel, isActualStaffPlan, scenarioWorkspace } from '@rc/engine';
import { api } from '../lib/api';
import type { ModelSummary } from '../lib/api';
import { toast } from './Toast';

interface Props {
  client: Client;
  scenarios: Scenario[];
  staffPlans: StaffPlan[];
  models: ModelSummary[];
  geoclientId?: string;
  /** Pre-selects Basic or Advanced when opened from a section-specific New button. */
  initialWorkspace?: ScenarioWorkspace;
  onClose: () => void;
  onCreated: (created: Scenario) => void;
}

const BLANK = '__blank__';

function newScenarioUuid(): string {
  return crypto.randomUUID();
}

export default function NewScenarioDialog({
  client,
  scenarios,
  staffPlans,
  models,
  geoclientId,
  initialWorkspace = 'advanced',
  onClose,
  onCreated,
}: Props) {
  const sortedScenarios = useMemo(
    () =>
      [...scenarios]
        .filter((s) => scenarioWorkspace(s) === initialWorkspace)
        .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')),
    [scenarios, initialWorkspace],
  );

  const actualPlans = useMemo(
    () =>
      staffPlans
        .filter((p) => isActualStaffPlan(p))
        .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')),
    [staffPlans],
  );

  const defaultName = `Scenario ${scenarios.length + 1}`;
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  const [workspace] = useState<ScenarioWorkspace>(initialWorkspace);
  const [modelId, setModelId] = useState<string>(
    sortedScenarios[0]?.modelId ?? models[0]?.id ?? '',
  );
  const [staffPlanId, setStaffPlanId] = useState<string>(actualPlans[0]?.id ?? '');
  const [templateId, setTemplateId] = useState<string>(
    sortedScenarios[0]?.id || BLANK,
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast('error', 'Scenario name is required.');
      return;
    }
    setBusy(true);
    try {
      const newId = newScenarioUuid();
      const now = new Date().toISOString();
      let draft: Scenario;

      if (templateId !== BLANK) {
        const template =
          sortedScenarios.find((s) => s.id === templateId) ?? sortedScenarios[0];
        if (!template) throw new Error('Template scenario not found.');
        const tpl = await api.getScenario(client.id, template.id);
        draft = {
          ...tpl,
          id: newId,
          clientId: client.id,
          modelId: workspace === 'basic' ? tpl.modelId : modelId || tpl.modelId,
          workspace,
          name: trimmed,
          notes: notes.trim() || `Cloned from ${tpl.name}`,
          createdAt: now,
          updatedAt: now,
        };
        // When creating Basic from a clone, keep the template's attached plan.
        // When creating Advanced, keep full profile as cloned.
      } else if (workspace === 'basic') {
        if (!staffPlanId) {
          toast('error', 'Pick a staff plan for a Basic scenario.');
          setBusy(false);
          return;
        }
        const full = await api.getStaffPlan(client.id, staffPlanId);
        if (!isActualStaffPlan(full)) {
          toast('error', 'Basic scenarios need an Actual staff plan.');
          setBusy(false);
          return;
        }
        const resolvedModelId = full.modelId || modelId || models[0]?.id;
        if (!resolvedModelId) {
          toast('error', 'Pick a model (or attach a plan that has one).');
          setBusy(false);
          return;
        }
        const m = await api.getModel(resolvedModelId);
        const selections: Record<string, string | string[]> = {};
        for (const dim of m.dimensions || []) {
          if (dim.kind === 'multiselect') {
            selections[dim.id] = [];
            continue;
          }
          const def = dim.options[0];
          if (def) selections[dim.id] = def.value;
        }
        draft = {
          id: newId,
          clientId: client.id,
          modelId: resolvedModelId,
          workspace: 'basic',
          name: trimmed,
          notes: notes.trim() || 'Basic scenario from staff plan.',
          createdAt: now,
          updatedAt: now,
          profile: {
            totalBudget: 0,
            channelAllocation: {},
            selections,
            currentStaffByLevel: flattenStaffPlanToByLevel(full.cells || []),
            currentStaffPlanId: full.id,
            automationBasis: 'actual',
          },
        };
      } else {
        if (!modelId) {
          toast('error', 'Pick a model.');
          setBusy(false);
          return;
        }
        const m = await api.getModel(modelId);
        const preset =
          m.channelMixPresets?.find(
            (p) => p.id === 'brand_plus_performance' || p.id === 'balanced',
          ) || m.channelMixPresets?.[0];
        const active = (m.channels || []).filter((c) => c.active !== false);
        const evenShare = active.length > 0 ? 1 / active.length : 0;
        const allocation: Record<string, number> = {};
        for (const ch of m.channels || []) {
          allocation[ch.id] = preset?.allocation?.[ch.id] ?? evenShare;
        }
        const selections: Record<string, string | string[]> = {};
        for (const dim of m.dimensions || []) {
          if (dim.kind === 'multiselect') {
            selections[dim.id] = [];
            continue;
          }
          const def = dim.options[0];
          if (def) selections[dim.id] = def.value;
        }
        if (client.vertical) selections.vertical = client.vertical;
        if (client.market?.code) selections.geo = client.market.code;

        draft = {
          id: newId,
          clientId: client.id,
          modelId,
          workspace: 'advanced',
          name: trimmed,
          notes: notes.trim() || 'Starter scenario.',
          createdAt: now,
          updatedAt: now,
          profile: {
            totalBudget: 50_000_000,
            channelMixPresetId: m.channelMixPresets?.[0]?.id || 'custom',
            channelAllocation: allocation,
            selections,
            activeChannels: Object.fromEntries(
              (m.channels || []).map((c) => [c.id, true]),
            ),
            adopsSupport: { programmatic: true, digital_direct: true, ctv: true },
            currentStaffByLevel: {},
          },
        };
      }

      const saved = await api.createScenario(client.id, draft, geoclientId);
      onCreated(saved);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const templateScenario =
    templateId === BLANK
      ? null
      : sortedScenarios.find((s) => s.id === templateId);

  const cloneWorkspaceHint = templateScenario
    ? scenarioWorkspace(templateScenario)
    : null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4">
      <div className="card max-w-lg w-full">
        <div className="card-header">
          <h2 className="text-base font-semibold">New Scenario</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="label">Workspace</label>
            <div
              className={clsx(
                'rounded-lg border px-3 py-2',
                workspace === 'basic'
                  ? 'border-sky-200 bg-sky-50'
                  : 'border-brand-purple-200 bg-brand-purple-50',
              )}
            >
              <div className="text-sm font-semibold text-slate-800">
                {workspace === 'basic' ? 'Scenarios - Basic' : 'Scenarios - Advanced'}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {workspace === 'basic'
                  ? 'Staff plan + OGS proportion + automation'
                  : 'Full profile + budget-sized modeled staffing'}
              </div>
            </div>
          </div>

          <div>
            <label className="label flex items-center gap-1.5">
              <FilePlus2 size={12} className="text-brand-purple-500" />
              Start from
            </label>
            <select
              className="input"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value={BLANK}>
                {workspace === 'basic'
                  ? 'Blank Basic scenario'
                  : 'Blank scenario (engine defaults)'}
              </option>
              {sortedScenarios.length > 0 && (
                <optgroup label="Clone existing scenario">
                  {sortedScenarios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
              <Copy size={11} className="text-slate-400 shrink-0 mt-px" />
              {templateScenario
                ? `Inputs will be copied from "${templateScenario.name}" (${cloneWorkspaceHint}).`
                : workspace === 'basic'
                  ? 'Creates a Basic scenario from a saved Actual staff plan.'
                  : "Builds a starter Advanced profile with the model's default channel mix."}
            </p>
          </div>

          <div>
            <label className="label">Scenario name</label>
            <input
              autoFocus
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aggressive growth, FY26 baseline"
            />
          </div>

          {workspace === 'basic' && templateId === BLANK ? (
            <div>
              <label className="label">Staff plan</label>
              <select
                className="input"
                value={staffPlanId}
                onChange={(e) => setStaffPlanId(e.target.value)}
              >
                {actualPlans.length === 0 && (
                  <option value="">No Actual staff plans yet</option>
                )}
                {actualPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Model is taken from the selected plan. Impact is measured from this
                roster after OGS proportion and automation.
              </p>
            </div>
          ) : workspace === 'advanced' ? (
            <div>
              <label className="label">Model</label>
              <select
                className="input"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Each Advanced scenario picks its own model.
              </p>
            </div>
          ) : null}

          <div>
            <label className="label">
              Notes <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What is this scenario testing?"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create scenario'}
          </button>
        </div>
      </div>
    </div>
  );
}
